
import React from 'react';
import { SimulationConfig, CollisionEffectType, CollisionColorMode } from '@/types';
import { SectionLabel } from './SectionLabel';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { Lock } from 'lucide-react';

interface ImpactFXSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  getLockInfo?: (id: string) => { isLocked: boolean; min_plan?: string };
}

export const ImpactFXSection: React.FC<ImpactFXSectionProps> = ({ config, setConfig, isLocked, getLockInfo }) => {
  const effectCategories = [
    { name: 'Classic', effects: ['SPARKS', 'RIPPLE', 'SMOKE_PUFF', 'BUBBLE_POP', 'FIREWORKS', 'CONFETTI_CANNON'] },
    { name: 'Elemental', effects: ['LIQUID_SPLASH', 'PLASMA_BLOB', 'LIGHTNING_BOLT', 'CRYSTAL_SHARDS', 'INK_SPLAT'] },
    { name: 'Cyber & Tech', effects: ['CYBER_HEX', 'MATRIX_RAIN', 'NEON_PULSE', 'DNA_HELIX', 'GLITCH_FRAGMENTS'] },
    { name: 'Cosmic & Physics', effects: ['SUPERNOVA', 'VORTEX_SUCK', 'GRAVITY_PARTICLES', 'ORBITAL_SPIN', 'SONIC_WAVE', 'SPIRAL_GALAXY', 'QUANTUM_TUNNEL', 'ELASTIC_RING'] },
    { name: 'Destruction', effects: ['SHATTER_GLASS', 'PIXEL_CRUMBLE', 'GHOST_SOULS'] }
  ];

  const colorModes: CollisionColorMode[] = ['BALL_COLOR', 'RAINBOW', 'CUSTOM_SOLID', 'CUSTOM_GRADIENT'];
  const blastColorLocked = getLockInfo?.('blastFXColor').isLocked;

  const ColorInput = ({ label, value, onChange, settingKey }: { label: string, value: string, onChange: (v: string) => void, settingKey?: string }) => (
    <ControlGroup label={label} settingKey={settingKey} darkMode={config.darkMode}>
        <div className={`flex items-center gap-2 border p-2 rounded-xl transition-all relative ${blastColorLocked ? 'opacity-40 grayscale pointer-events-none' : ''} ${config.darkMode ? 'border-white/5' : 'border-neutral-200 shadow-sm'}`}>
            {blastColorLocked && <div className="absolute inset-x-[-0.5rem] inset-y-[-0.25rem] z-10 bg-white/5 backdrop-blur-[1px]" />}
            <div className={`w-6 h-6 rounded-full border shadow-sm ${config.darkMode ? 'border-white/20' : 'border-neutral-300'}`} style={{ backgroundColor: value }} />
            <div className="relative flex-1">
                 <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" />
                 <input type="text" value={value} onChange={e => onChange(e.target.value)} className={`w-full px-2 py-1 text-[9px] font-mono border rounded-lg ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`} />
            </div>
        </div>
    </ControlGroup>
  );

  return (
    <div className="w-full flex flex-col gap-3 mb-4">
      <div className="flex items-center justify-between">
          <span className="text-[10px] font-black uppercase text-pink-400">Impact Visuals</span>
          <span className="bg-pink-600 text-white text-[8px] font-black uppercase px-2 py-0.5 rounded-full animate-pulse">New!</span>
      </div>
      
      <ControlGroup label="Select Effect Style" settingKey="collisionEffect" darkMode={config.darkMode}>
        <select 
            value={config.collisionEffect || 'RIPPLE'} 
            onChange={e => setConfig(p => ({...p, collisionEffect: e.target.value as CollisionEffectType}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {effectCategories.map(cat => (
                <optgroup key={cat.name} label={cat.name} className={`${config.darkMode ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-900'} font-bold italic`}>
                    {cat.effects.map(e => <option key={e} value={e} className={`${config.darkMode ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-900'} font-normal not-italic`}>{e.replace('_', ' ')}</option>)}
                </optgroup>
            ))}
        </select>
      </ControlGroup>

      <div className="flex flex-col gap-1">
          <SmartInput label="Duration (ms)" min={100} max={2000} value={config.collisionEffectDuration || 800} onChange={v => setConfig(p => ({...p, collisionEffectDuration: v}))} darkMode={config.darkMode} settingKey="collisionEffectDuration" fullWidth />
          <SmartInput label="Particles" min={1} max={100} value={config.collisionEffectParticleCount || 20} onChange={v => setConfig(p => ({...p, collisionEffectParticleCount: v}))} darkMode={config.darkMode} settingKey="collisionEffectParticleCount" fullWidth />
          <SmartInput label="Scale" min={0.1} max={5.0} step={0.1} value={config.collisionEffectScale || 1.0} onChange={v => setConfig(p => ({...p, collisionEffectScale: v}))} darkMode={config.darkMode} settingKey="collisionEffectScale" fullWidth />
      </div>
      <SmartInput label="Gravity" min={0} max={2} step={0.1} value={config.collisionEffectGravity ?? 0.5} onChange={v => setConfig(p => ({...p, collisionEffectGravity: v}))} darkMode={config.darkMode} settingKey="collisionEffectGravity" fullWidth />

      <div className={`h-px my-1 ${config.darkMode ? 'bg-white/10' : 'bg-black/5'}`} />
      <span className="text-[9px] font-black uppercase opacity-60">Effect Coloring</span>
      
      <ControlGroup label="Color Mode" settingKey="collisionEffectColorMode" darkMode={config.darkMode}>
        <select 
            value={config.collisionEffectColorMode || 'BALL_COLOR'} 
            onChange={e => setConfig(p => ({...p, collisionEffectColorMode: e.target.value as CollisionColorMode}))}
            className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
        >
            {colorModes.map(m => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
        </select>
      </ControlGroup>

      {(config.collisionEffectColorMode === 'CUSTOM_SOLID' || config.collisionEffectColorMode === 'CUSTOM_GRADIENT') && (
          <ColorInput label="Primary Color" value={config.collisionEffectColor1 || '#ff0000'} onChange={v => setConfig(p => ({...p, collisionEffectColor1: v}))} settingKey="collisionEffectColor1" />
      )}
      
      {config.collisionEffectColorMode === 'CUSTOM_GRADIENT' && (
          <ColorInput label="Secondary Color" value={config.collisionEffectColor2 || '#ffff00'} onChange={v => setConfig(p => ({...p, collisionEffectColor2: v}))} settingKey="collisionEffectColor2" />
      )}
    </div>
  );
};
